// TableHeaderCell.jsx

import React, { useRef } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './TableHeaderCell.css';

// Components
import ContextMenu from 'components/contextmenu/ContextMenu';

/**
 * TableHeaderCell
 * 
 * Renders a header cell for the table view, with support for resizing and drag-and-drop reordering.
 * 
 * @param {object} column - The column definition object, containing key, label, width, etc.
 * @param {function} onResizeStart - Handler for when resizing starts, receives (event, columnKey).
 * @param {boolean} isSortable - Whether this column should expose sort actions.
 * @param {function} onSortAscending - Handler to apply ascending sort for a column key.
 * @param {function} onSortDescending - Handler to apply descending sort for a column key.
 * @param {string} draggingKey - The key of the column currently being dragged, if any.
 * @param {string} dragOverKey - The key of the column currently being dragged over, if any.
 * @param {'click'|'contextmenu'} contextMenuTrigger - Interaction used to open the context menu.
 * @param {function} onDragStart - Handler for when dragging starts, receives (columnKey, event).
 * @param {function} onDragOver - Handler for when a column is dragged over this cell, receives (columnKey, event).
 * 
 * returns {ReactNode} - The rendered header cell component.
 * 
 * Note: The 'title' column is not draggable to maintain a consistent user experience.
 *
 * @returns {ReactNode} The rendered component.
 */
const TableHeaderCell = ({
    column,
    onResizeStart,
    isSortable = false,
    onSortAscending,
    onSortDescending,
    contextMenuTrigger = 'contextmenu',
    draggingKey,
    dragOverKey,
    onDragStart,
    onDragOver,
    onDrop,
    onDragEnd,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- REFS ----- //

    const headerCellRef = useRef(null);

    const canDrag = column?.key && column.key !== 'title';
    const isDragging = column?.key && draggingKey === column.key;
    const isDropTarget = column?.key && dragOverKey === column.key;

    // ----- HANDLERS ----- //

    /**
 * Handles sort ascending.
 */

    const handleSortAscending = () => {
        if (!column?.key) return;
        if (typeof onSortAscending === 'function') {
            onSortAscending(column.key);
        }
    };

    /**
 * Handles sort descending.
 */
    const handleSortDescending = () => {
        if (!column?.key) return;
        if (typeof onSortDescending === 'function') {
            onSortDescending(column.key);
        }
    };

    /**
 * Handles drag start.
 *
 * @param {*} event
 */
    const handleDragStart = (event) => {
        if (!canDrag) return;
        if (event.target?.closest?.('.table-view-column-resizer')) {
            event.preventDefault();
            return;
        }
        const dragImage = event.currentTarget.cloneNode(true);
        dragImage.style.position = 'absolute';
        dragImage.style.top = '-1000px';
        dragImage.style.left = '-1000px';
        dragImage.style.opacity = '0.85';
        dragImage.style.pointerEvents = 'none';
        document.body.appendChild(dragImage);
        event.dataTransfer.setDragImage(dragImage, 12, 12);
        onDragStart(column.key, event);
        setTimeout(() => {
            if (dragImage?.parentNode) dragImage.parentNode.removeChild(dragImage);
        }, 0);
    };

    // ----- RENDER ----- //

    return (
        <>
            {/* DRAGGING */}
            <div
              className={`table-view-header-cell-container ${!canDrag ? 'no-drag' : ''} ${isDragging ? 'dragging' : ''} ${isDropTarget ? 'drop-target' : ''}`}
              style={{
                backgroundColor: isDragging
                        ? theme.backgroundHover
                        : isDropTarget
                            ? theme.backgroundMuted
                            : 'transparent',
                boxShadow: isDropTarget ? `inset -2px 0 0 ${theme.foregroundMuted}` : 'none',
                width: column.width || 200,
              }}
              ref={headerCellRef}
              draggable={Boolean(canDrag)}
              onDragEnd={canDrag ? onDragEnd : undefined}
              onDragOver={canDrag ? (event) => onDragOver(column.key, event) : undefined}
              onDragStart={canDrag ? handleDragStart : undefined}
              onDrop={canDrag ? (event) => onDrop(column.key, event) : undefined}
            >
                {/* TABLE VIEW HEADER CELL LABEL */}
                <span className="table-view-header-cell-label">
                    {column.label}
                </span>
                {/* TABLE VIEW COLUMN RESIZER */}
                <div
                  className="table-view-column-resizer"
                  onMouseDown={(event) => onResizeStart(event, column.key)}
                />
            </div>

            <ContextMenu
              triggerRef={headerCellRef}
              triggerMode={contextMenuTrigger}
            >
                {isSortable ? (
                    <ContextMenu.Item iconKey="sort" label="Sort">
                        <ContextMenu.Item
                          iconKey="arrow_warm_up"
                          label="Ascending"
                          onClick={handleSortAscending}
                        />
                        <ContextMenu.Item
                          iconKey="arrow_cool_down"
                          label="Descending"
                          onClick={handleSortDescending}
                        />
                    </ContextMenu.Item>
                ) : null}
            </ContextMenu>
        </>
    );
};

// Dev Tools
TableHeaderCell.displayName = 'TableHeaderCell';

export default TableHeaderCell;
